import { FormPage } from "#components/ui/FormPage.tsx";
import { PreventNavigationModal } from "#components/ui/PreventNavigationModal.tsx";
import { useGetPersonalizationSuspenseQuery } from "#features/personalization/api/useGetPersonalizationSuspenseQuery.ts";
import { useUpdatePersonalizationMutation } from "#features/personalization/api/useUpdatePersonalizationMutation.ts";
import { Personality } from "#features/personalization/components/Personality.tsx";
import { SavedMemories } from "#features/personalization/components/SavedMemories.tsx";
import { TrackingOptOutSwitch } from "#features/personalization/components/TrackingOptOutSwitch.tsx";
import { usePersonalizationTracking } from "#features/personalization/hooks/usePersonalizationTracking.ts";
import { type UpdatePersonalizationForm, useUpdatePersonalizationForm } from "#features/personalization/hooks/useUpdatePersonalizationForm.ts";
import type { UpdatePersonalizationCommandMemory } from "#types/types.ts";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import { Button, ButtonGroup, Content, Div, Divider, type DivProps, Flex } from "@hopper-ui/components";
import { type ReactElement, useCallback, useEffect, useMemo } from "react";
import { FormProvider } from "react-hook-form";
import { toast } from "react-toastify";

interface Props extends DivProps {
    header?: ReactElement;
}

export const PersonalizationForm = ({ header, ...rest }: Props) => {
    const { tShared } = useSharedTranslation();
    const { data: personalization } = useGetPersonalizationSuspenseQuery();
    const { mutate, isPending } = useUpdatePersonalizationMutation();
    const { trackPersonalizationChanges } = usePersonalizationTracking();

    const defaultValues = useMemo<UpdatePersonalizationForm>(() => ({
        personality: personalization.personality,
        memories: personalization.memories.map(memory => ({
            memoryId: memory.id,
            value: memory.value
        })),
        trackingOptOut: personalization.trackingOptOut
    }), [personalization]);

    const form = useUpdatePersonalizationForm(defaultValues);
    const { handleSubmit, reset, formState } = form;

    // Reset the form after personalization data is refreshed following a mutation
    useEffect(() => {
        reset(defaultValues);
    }, [personalization, reset, defaultValues]);

    const onSubmit = useCallback((data: UpdatePersonalizationForm) => {
        const memories = data.memories.map<UpdatePersonalizationCommandMemory>(memory => {
            const originalMemory = personalization.memories.find(m => m.id === memory.memoryId);

            // Set source to User if the memory is new or modified
            if (!originalMemory || originalMemory.value !== memory.value) {
                return {
                    id: memory.memoryId,
                    value: memory.value,
                    source: "user"
                };
            }

            return originalMemory;
        });

        mutate({
            personality: data.personality,
            memories,
            trackingOptOut: data.trackingOptOut ?? personalization.trackingOptOut
        }, {
            onSuccess: () => {
                trackPersonalizationChanges(data, personalization);
                toast.success(tShared("personalization.updateSuccess"));
            },
            onError: () => {
                toast.error(tShared("personalization.updateError"));
            }
        });
    }, [mutate, personalization, tShared, trackPersonalizationChanges]);

    return (
        <>
            <FormProvider {...form}>
                <FormPage
                    onSubmit={handleSubmit(onSubmit)}
                    content={
                        <Content padding={header ? "inset-lg" : "core_0"}>
                            <Flex
                                direction="column"
                                borderRadius="rounded-md"
                                gap="inline-xl"
                                {...rest}
                            >
                                {header && <Div paddingX="inset-xl">{header}</Div>}
                                <Div paddingX="inset-xl"><TrackingOptOutSwitch /></Div>
                                <Divider />
                                <Div paddingX="inset-xl"><Personality /></Div>
                                <Divider />
                                <Div paddingX="inset-xl"><SavedMemories /></Div>
                            </Flex>
                        </Content>
                    }
                    footer={
                        formState.isDirty &&
                            <ButtonGroup isDisabled={isPending}>
                                <Button variant="secondary" onPress={() => reset()}>
                                    {tShared("personalization.discard")}
                                </Button>
                                <Button type="submit" isLoading={isPending}>
                                    {tShared("personalization.submit")}
                                </Button>
                            </ButtonGroup>
                    }
                />
            </FormProvider>
            <PreventNavigationModal isDisabled={!formState.isDirty || isPending} onDiscard={reset} />
        </>
    );
};
